// Registro de reports no Data Lake para a IA (MCP).
//
// Um "report" = uma folha de menu do portal. Publicar no Lake significa gravar
// um report.json (só ponteiros) em:
//     infra/reports/<area>/<slug>/report.json
// Os tools de report (report_context / report_code / report_data) resolvem o
// report pelo nome/alias, leem o código-fonte ao vivo dos `origins` (git) e a
// versão publicada ao vivo (via report.url). Não há snapshot nem doc.txt.

import {
  ensureDirFileShare,
  putFileShareJson,
  deleteFileShare,
  getFileShareText,
  LAKE_BASE_URL,
} from "./fileshare";

const PORTAL_ORIGIN = (process.env.REACT_APP_URL || window.location.origin).replace(
  /\/+$/,
  ""
);
export const REPORTS_ROOT = "infra/reports";

// "Pricing Dashboard" -> "pricing-dashboard"
export const slugify = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// último segmento do path de menu: "A | B | Nome " -> "Nome"
export const reportNameFromMenu = (menuPath) => {
  const parts = (menuPath || "").split("|");
  return (parts[parts.length - 1] || "").trim();
};

// Reproduz a URL do portal: /admin/<area>/<último segmento cru>/<idMenu>
// (o segmento mantém os espaços, igual ao que o portal gera).
export const buildReportUrl = (area, menuPath, menuId) => {
  const parts = (menuPath || "").split("|");
  const rawLast = parts[parts.length - 1];
  return `${PORTAL_ORIGIN}/admin/${area}/${encodeURIComponent(rawLast)}/${menuId}`;
};

// Caminho da pasta do report no Lake.
export const seedDir = (area, slug) =>
  `${REPORTS_ROOT}/${slugify(area)}/${slug}`;

// Monta o report.json enxuto (name + aliases + url + origins).
export const buildSeed = ({ name, aliases, url, repoUrl, branch }) => {
  const seed = { report: { name } };
  const aliasList = (aliases || "")
    .split(",")
    .map((a) => a.trim())
    .filter(Boolean);
  if (aliasList.length) seed.report.aliases = aliasList;
  seed.report.url = url;
  seed.origins = [
    { type: "azure-devops-git", url: repoUrl, branch: branch || "main" },
  ];
  return seed;
};

// Lê um report.json existente (ou null se não houver).
export const readSeed = async (area, slug) => {
  try {
    const txt = await getFileShareText(
      `${seedDir(area, slug)}/report.json`,
      LAKE_BASE_URL
    );
    return JSON.parse(txt);
  } catch (e) {
    return null;
  }
};

// Grava (cria/sobrescreve) o report.json.
//
// O write no Azure Files é em 2 etapas (cria com tamanho + escreve o range). Se
// a 2a etapa falhar/for interrompida, sobra um arquivo zero-padded que o MCP nao
// consegue parsear (json.loads) e o report vira invisivel sem erro nenhum.
// Por isso gravamos com verificacao: le de volta, exige JSON valido e identico
// ao esperado, e refaz ate 3x. Se nao conseguir, lanca erro (o handleSubmit
// exibe o toast e NAO persiste no banco -> sem divergencia banco x Lake).
export const publishReportSeed = async ({ area, slug, seed }) => {
  const dir = seedDir(area, slug);
  const path = `${dir}/report.json`;
  await ensureDirFileShare(dir, LAKE_BASE_URL);

  const expected = JSON.stringify(seed, null, 2);
  let lastErr;
  for (let attempt = 1; attempt <= 3; attempt++) {
    await putFileShareJson(path, seed, LAKE_BASE_URL);
    try {
      const written = await getFileShareText(path, LAKE_BASE_URL);
      JSON.parse(written); // tem que ser JSON valido (pega zero-padding/truncamento)
      if (written === expected) return path; // gravado integro
      lastErr = new Error("conteudo gravado diverge do esperado");
    } catch (e) {
      lastErr = e; // JSON invalido ou falha ao reler
    }
  }
  throw new Error(
    `Nao foi possivel gravar o report.json integro no Lake apos 3 tentativas: ${
      lastErr?.message || lastErr
    }`
  );
};

// Remove o report.json (desregistra o report da IA).
export const unpublishReportSeed = async ({ area, slug }) => {
  const path = `${seedDir(area, slug)}/report.json`;
  await deleteFileShare(path, LAKE_BASE_URL);
  return path;
};
